/**
 * CoWork mode tabs — the desktop-only [ Chat · CoWork ] switch at the top of the
 * sidebar (hybrid CoWork). Switching to CoWork reveals the folder panel + routes
 * chat turns to the local machine; Chat is the normal cloud experience.
 *
 * Renders nothing in the web app.
 *
 * @module components/cowork/cowork-mode-tabs
 */

import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HardDrive, MessageSquare } from "lucide-react";

import { useCowork } from "@/hooks/use-cowork";
import { cn } from "@/lib/utils";
import type { CoworkMode } from "@/store/cowork-store";

export function CoworkModeTabs({ collapsed }: { collapsed?: boolean }) {
  const { desktop, mode, setMode } = useCowork();
  const navigate = useNavigate();
  const { appId: routeAppId } = useParams<{ appId: string }>();

  // Switching modes always lands on a fresh new-task screen (like clicking New
  // Task), so you never stay on the previous mode's open chat.
  const switchMode = useCallback(
    (next: CoworkMode) => {
      if (next === mode) return;
      setMode(next);
      let appId = routeAppId;
      try {
        appId ||= localStorage.getItem("taqat_last_active_app_id") || localStorage.getItem("taqat_default_app_id") || undefined;
      } catch {
        /* ignore */
      }
      navigate(`/app/${appId || ""}/chat`);
    },
    [mode, setMode, navigate, routeAppId],
  );

  if (!desktop) return null;

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1 px-1 py-1.5">
        <IconTab active={mode === "chat"} label="Chat" onClick={() => switchMode("chat")} Icon={MessageSquare} />
        <IconTab active={mode === "cowork"} cowork label="CoWork" onClick={() => switchMode("cowork")} Icon={HardDrive} />
      </div>
    );
  }

  return (
    <div className="mb-1 mt-2 flex gap-1 rounded-lg border border-border bg-muted/60 p-1">
      <Tab active={mode === "chat"} onClick={() => switchMode("chat")}>
        <MessageSquare className="size-3.5" />
        Chat
      </Tab>
      <Tab active={mode === "cowork"} cowork onClick={() => switchMode("cowork")}>
        <HardDrive className="size-3.5" />
        CoWork
      </Tab>
    </div>
  );
}

function Tab({
  active,
  cowork,
  onClick,
  children,
}: {
  active: boolean;
  cowork?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors",
        active
          ? cowork
            ? "bg-background text-secondary shadow-sm"
            : "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function IconTab({
  active,
  cowork,
  label,
  onClick,
  Icon,
}: {
  active: boolean;
  cowork?: boolean;
  label: string;
  onClick: () => void;
  Icon: typeof HardDrive;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-pressed={active}
      className={cn(
        "grid size-8 place-items-center rounded-md transition-colors",
        active
          ? cowork
            ? "bg-secondary/15 text-secondary"
            : "bg-accent text-foreground"
          : "text-muted-foreground hover:bg-accent/60",
      )}
    >
      <Icon className="size-4" />
    </button>
  );
}
